// ARIA tree (virtualised): roving focus, arrow keys, Home and End, Enter to select, type-ahead, lazy children loaded on expand.
import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronRightIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

import { cn } from "@/lib/cn";

export interface TreeNodeState {
  level: number;
  expanded: boolean;
  selected: boolean;
  loading: boolean;
  hasChildren: boolean;
}

interface FlatNode<T> {
  node: T;
  id: string;
  level: number;
  parentId: string | null;
  setSize: number;
  posInSet: number;
  hasChildren: boolean;
  expanded: boolean;
  loading: boolean;
}

interface TreeViewProps<T> {
  roots: readonly T[];
  getId: (node: T) => string;
  getLabel: (node: T) => string;
  hasChildren: (node: T) => boolean;
  childrenOf: (id: string) => readonly T[] | undefined;
  expanded: ReadonlySet<string>;
  onExpandedChange: (id: string, expanded: boolean) => void;
  selectedId: string | null;
  onSelect: (node: T) => void;
  renderNode: (node: T, state: TreeNodeState) => ReactNode;
  ariaLabel: string;
  rowHeight?: number;
  className?: string;
}

function flatten<T>(
  props: Pick<TreeViewProps<T>, "roots" | "getId" | "hasChildren" | "childrenOf" | "expanded">,
): FlatNode<T>[] {
  const result: FlatNode<T>[] = [];
  const visit = (nodes: readonly T[], level: number, parentId: string | null) => {
    nodes.forEach((node, index) => {
      const id = props.getId(node);
      const hasChildren = props.hasChildren(node);
      const expanded = hasChildren && props.expanded.has(id);
      const children = expanded ? props.childrenOf(id) : undefined;
      result.push({
        node,
        id,
        level,
        parentId,
        setSize: nodes.length,
        posInSet: index + 1,
        hasChildren,
        expanded,
        loading: expanded && children === undefined,
      });
      if (children) {
        visit(children, level + 1, id);
      }
    });
  };
  visit(props.roots, 1, null);
  return result;
}

export function TreeView<T>({
  roots,
  getId,
  getLabel,
  hasChildren,
  childrenOf,
  expanded,
  onExpandedChange,
  selectedId,
  onSelect,
  renderNode,
  ariaLabel,
  rowHeight = 32,
  className,
}: TreeViewProps<T>) {
  const rows = useMemo(
    () => flatten({ roots, getId, hasChildren, childrenOf, expanded }),
    [roots, getId, hasChildren, childrenOf, expanded],
  );
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const activeId =
    focusedId !== null && rows.some((row) => row.id === focusedId)
      ? focusedId
      : (selectedId ?? rows[0]?.id ?? null);
  const typeahead = useRef({ text: "", at: 0 });
  const scrollRef = useRef<HTMLDivElement>(null);
  const shouldFocus = useRef(false);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  });

  const indexOf = useCallback((id: string | null) => rows.findIndex((row) => row.id === id), [rows]);

  const moveTo = useCallback(
    (index: number) => {
      const row = rows[index];
      if (row === undefined) {
        return;
      }
      shouldFocus.current = true;
      setFocusedId(row.id);
      virtualizer.scrollToIndex(index, { align: "auto" });
    },
    [rows, virtualizer],
  );

  useEffect(() => {
    if (!shouldFocus.current || activeId === null) {
      return;
    }
    const element = scrollRef.current?.querySelector<HTMLElement>(`[data-tree-id="${CSS.escape(activeId)}"]`);
    if (!element) {
      return;
    }
    shouldFocus.current = false;
    element.focus({ preventScroll: true });
  });

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const index = indexOf(activeId);
    const row = rows[index];
    if (row === undefined) {
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        moveTo(Math.min(rows.length - 1, index + 1));
        break;
      case "ArrowUp":
        moveTo(Math.max(0, index - 1));
        break;
      case "Home":
        moveTo(0);
        break;
      case "End":
        moveTo(rows.length - 1);
        break;
      case "ArrowRight":
        if (row.hasChildren && !row.expanded) {
          onExpandedChange(row.id, true);
        } else if (row.expanded) {
          moveTo(index + 1);
        }
        break;
      case "ArrowLeft":
        if (row.expanded) {
          onExpandedChange(row.id, false);
        } else if (row.parentId !== null) {
          moveTo(indexOf(row.parentId));
        }
        break;
      case "Enter":
      case " ":
        onSelect(row.node);
        break;
      default: {
        if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) {
          return;
        }
        const now = Date.now();
        typeahead.current = {
          text:
            now - typeahead.current.at > 700
              ? event.key.toLowerCase()
              : typeahead.current.text + event.key.toLowerCase(),
          at: now,
        };
        const query = typeahead.current.text;
        const ordered = [...rows.slice(index + 1), ...rows.slice(0, index + 1)];
        const match = ordered.find((candidate) => getLabel(candidate.node).toLowerCase().startsWith(query));
        if (match) {
          moveTo(indexOf(match.id));
        }
        break;
      }
    }
    event.preventDefault();
  };

  return (
    <div ref={scrollRef} className={cn("relative min-h-0 pane-scroll", className)}>
      <div
        role="tree"
        aria-label={ariaLabel}
        className="relative w-full outline-none"
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualizer.getVirtualItems().map((item) => {
          const row = rows[item.index];
          if (row === undefined) {
            return null;
          }
          const selected = row.id === selectedId;
          return (
            <div
              key={row.id}
              role="treeitem"
              data-tree-id={row.id}
              aria-level={row.level}
              aria-setsize={row.setSize}
              aria-posinset={row.posInSet}
              aria-expanded={row.hasChildren ? row.expanded : undefined}
              aria-selected={selected}
              aria-busy={row.loading || undefined}
              tabIndex={row.id === activeId ? 0 : -1}
              onKeyDown={onKeyDown}
              onFocus={() => {
                setFocusedId(row.id);
              }}
              onClick={(event) => {
                setFocusedId(row.id);
                if (
                  row.hasChildren &&
                  event.target instanceof Element &&
                  event.target.closest("[data-tree-toggle]")
                ) {
                  onExpandedChange(row.id, !row.expanded);
                } else {
                  onSelect(row.node);
                }
              }}
              className={cn(
                "absolute top-0 left-0 flex w-full cursor-pointer items-center gap-1 rounded-sm pr-2 text-ui outline-none hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring",
                selected && "bg-selected text-selected-foreground",
              )}
              style={{
                transform: `translateY(${item.start}px)`,
                height: rowHeight,
                paddingLeft: (row.level - 1) * 14 + 4,
              }}
            >
              <span
                aria-hidden
                data-tree-toggle
                className="grid size-5 shrink-0 place-items-center text-muted-foreground"
              >
                {row.hasChildren ? (
                  <ChevronRightIcon
                    className={cn("size-3.5 transition-transform duration-150", row.expanded && "rotate-90")}
                  />
                ) : null}
              </span>
              <span className="min-w-0 flex-1">
                {renderNode(row.node, {
                  level: row.level,
                  expanded: row.expanded,
                  selected,
                  loading: row.loading,
                  hasChildren: row.hasChildren,
                })}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
